import { useState } from 'react';
import { toast } from 'sonner';
import { GitPullRequest, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';

/**
 * Turns a compare-page diff into an approvable change-list for the
 * Dev → Test → Prod pipeline. `resourceType` is 'semantic_model' or 'insight';
 * `diff` is the full result object from the compare endpoint.
 */
export function PromoteDiffButton({ resourceType, diff, disabled }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('production');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.createChangeList({ resource_type: resourceType, target_stage: stage, note, diff });
      toast.success(`Change-list created (${res.change_list_id}) — sent for approval`);
      setOpen(false); setNote('');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={disabled} className="rounded-lg gap-1.5" data-testid="promote-diff-btn">
        <GitPullRequest className="w-3.5 h-3.5" /> Promote differences
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="promote-diff-dialog">
          <DialogHeader><DialogTitle className="font-heading">Promote differences</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Creates an approvable change-list from this comparison. A reviewer can approve or reject it before the changes move forward.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Target stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="promote-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note for reviewer (optional)</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Why these changes should be promoted…" className="text-sm" data-testid="promote-note-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="rounded-lg" data-testid="promote-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitPullRequest className="w-4 h-4 mr-2" />} Request approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
